import { useState } from 'react';
import { Download } from 'lucide-react';
import { EXPORT_DATASETS, type ExportDataset, type ExportFormat } from '@ederest/contracts';
import { download } from '../api/client.js';
import { Button } from './Button.js';
import './export-menu.css';

const DATASET_LABELS: Record<ExportDataset, string> = {
  'friction-points': 'Points de friction',
  funnel: 'Entonnoirs',
  'field-metrics': 'Indicateurs par champ',
  feedback: 'Avis recueillis',
  'feedback-comments': 'Commentaires des usagers',
  comparison: 'Comparaison avant / après',
};

interface ExportMenuProps {
  projectId: string | undefined;
  queryString: string;
  /** The datasets offered; every dataset by default. */
  datasets?: readonly ExportDataset[];
}

/**
 * Extraction in open formats (functional §5.4): the filtered aggregates, CSV or
 * JSON. Every download is one audited call to the API.
 */
export function ExportMenu({ projectId, queryString, datasets = EXPORT_DATASETS }: ExportMenuProps) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const run = async (dataset: ExportDataset, format: ExportFormat) => {
    if (!projectId) return;
    const key = `${dataset}:${format}`;
    setBusy(key);
    setError(null);
    try {
      const query = new URLSearchParams(queryString);
      query.set('format', format);
      await download(`/analytics/projects/${projectId}/exports/${dataset}?${query.toString()}`);
      setOpen(false);
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "L'export a échoué");
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="export">
      <Button
        icon={<Download />}
        aria-expanded={open}
        aria-haspopup="menu"
        onClick={() => setOpen((v) => !v)}
      >
        Exporter
      </Button>
      {open && (
        <div className="export__menu" role="menu" aria-label="Exporter les données filtrées">
          {datasets.map((dataset) => (
            <div key={dataset} className="export__row">
              <span className="export__label">{DATASET_LABELS[dataset]}</span>
              <span className="export__formats">
                <button
                  type="button"
                  role="menuitem"
                  disabled={busy !== null}
                  onClick={() => void run(dataset, 'csv')}
                  aria-label={`${DATASET_LABELS[dataset]} en CSV`}
                >
                  {busy === `${dataset}:csv` ? '…' : 'CSV'}
                </button>
                <button
                  type="button"
                  role="menuitem"
                  disabled={busy !== null}
                  onClick={() => void run(dataset, 'json')}
                  aria-label={`${DATASET_LABELS[dataset]} en JSON`}
                >
                  {busy === `${dataset}:json` ? '…' : 'JSON'}
                </button>
              </span>
            </div>
          ))}
          <p className="export__hint">
            Données agrégées filtrées, jamais un événement brut. Chaque export est journalisé.
          </p>
          {error && (
            <p className="export__error" role="alert">
              {error}
            </p>
          )}
        </div>
      )}
    </div>
  );
}
